import { useState, useCallback, useMemo } from 'react';
import { Badge } from '../types/fingeringEditor';
import { useSyncedRef } from './useSyncedRef';

/** 矩形 drag 中の SVG 仮想座標 (caller 側で正規化は不要、 badgesInRect が min/max 計算)。 */
export interface SelectRectState {
  startSvgX: number;
  startSvgY: number;
  currentSvgX: number;
  currentSvgY: number;
  page: number;
}

export interface UseRangeSelectionResult {
  selectedIds: ReadonlySet<number>;
  primarySelectedId: number | null;
  isMultiSelect: boolean;
  selectedCount: number;
  // issue #16 v3 Phase A: selectMode は Core hook で editorMode + isMobile の派生として再構築
  // (!isMobile || editorMode==='move')。 以前ここに `selectMode` / `setSelectMode` があったが撤去済。

  // issue #45 バグ C (案 C-2): noExpand は expandChord をバイパスして単一 badge のみ選択する opt-out flag。
  //   §5 C-2 (chord=beat) は ChordNotePicker からの個別音符選択経路のみ例外、 matching/drag/削除/矩形選択は従来通り peer 展開。
  select: (id: number, opts?: { additive?: boolean; noExpand?: boolean }) => void;
  toggle: (id: number) => void;
  selectRange: (ids: number[], opts?: { additive?: boolean }) => void;
  clear: () => void;

  selectRectVisible: SelectRectState | null;
  setSelectRectVisible: (r: SelectRectState | null) => void;
}

/**
 * 与えられた ids を chord 単位 (同 beatIndex の peer) に展開する。
 * spec §4.3 C-2 ポリシー: chord = beat 単位で選択。
 */
function expandChord(badges: Badge[], ids: Iterable<number>): Set<number> {
  const byBeat = new Map<number, number[]>();
  for (const b of badges) {
    if (b.beatIndex == null) continue;
    const arr = byBeat.get(b.beatIndex) ?? [];
    arr.push(b.id);
    byBeat.set(b.beatIndex, arr);
  }
  const result = new Set<number>();
  const idsArr = Array.from(ids);
  for (const id of idsArr) {
    result.add(id);
    const b = badges.find((x) => x.id === id);
    if (b?.beatIndex != null) {
      const peers = byBeat.get(b.beatIndex) ?? [];
      for (const peerId of peers) result.add(peerId);
    }
  }
  return result;
}

export function useRangeSelection(badges: Badge[]): UseRangeSelectionResult {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(() => new Set());
  // primary は最後に select したもの (操作の起点を覚えるため別 state)。
  const [primarySelectedId, setPrimarySelectedId] = useState<number | null>(null);
  const [selectRectVisible, setSelectRectVisible] = useState<SelectRectState | null>(null);

  // chord 拡張で最新の badges を参照しつつ、 setSelectedIds の依存に入れないために ref で保持。
  const badgesRef = useSyncedRef(badges);

  const select = useCallback((id: number, opts?: { additive?: boolean; noExpand?: boolean }) => {
    const additive = opts?.additive ?? false;
    // issue #45 バグ C (案 C-2): noExpand=true で expandChord をバイパス → 単一 badge のみ選択。
    //   §5 C-2 (chord=beat) は他経路で維持。 ChordNotePicker 単一音符選択のみ opt-out。
    const noExpand = opts?.noExpand ?? false;
    setSelectedIds((prev) => {
      const base = additive ? new Set(prev) : new Set<number>();
      base.add(id);
      return noExpand ? base : expandChord(badgesRef.current, base);
    });
    setPrimarySelectedId(id);
  }, [badgesRef]);

  const toggle = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const expanded = expandChord(badgesRef.current, [id]);
      // 既に含まれていれば「peer ごと削除」、 含まれていなければ「peer ごと追加」。
      const isSelected = prev.has(id);
      const next = new Set(prev);
      const peers = Array.from(expanded);
      if (isSelected) {
        for (const peerId of peers) next.delete(peerId);
      } else {
        for (const peerId of peers) next.add(peerId);
      }
      return next;
    });
    setPrimarySelectedId((prev) => (prev === id ? null : id));
  }, [badgesRef]);

  const selectRange = useCallback((ids: number[], opts?: { additive?: boolean }) => {
    const additive = opts?.additive ?? false;
    setSelectedIds((prev) => {
      const base = additive ? new Set(prev) : new Set<number>();
      for (const id of ids) base.add(id);
      return expandChord(badgesRef.current, base);
    });
    // 範囲選択の primary は最初の id を採用 (空配列なら null)。
    setPrimarySelectedId(ids.length > 0 ? ids[0] : null);
  }, [badgesRef]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setPrimarySelectedId(null);
  }, []);

  const isMultiSelect = selectedIds.size > 1;
  const selectedCount = selectedIds.size;

  return useMemo(
    () => ({
      selectedIds,
      primarySelectedId,
      isMultiSelect,
      selectedCount,
      select,
      toggle,
      selectRange,
      clear,
      selectRectVisible,
      setSelectRectVisible,
    }),
    [
      selectedIds, primarySelectedId, isMultiSelect, selectedCount,
      selectRectVisible,
      select, toggle, selectRange, clear,
    ],
  );
}
